import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  User, Mail, Phone, MapPin, Briefcase, Star, Clock, Settings,
  CheckCircle, XCircle, Loader2, Crown, DollarSign, TrendingUp,
  FileText, MessageSquare, Bell, LogOut, Edit, Camera, Plus, Send,
  Droplet, Zap, Thermometer, Wrench, Sparkles, Car, BadgeCheck, Shield, Upload, Image, Trash2,
  Calendar, Calculator, Home, Bug, Hammer, Trees, Truck, DoorOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import QuoteBuilder from "@/components/QuoteBuilder";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import NotificationBell from "@/components/NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
  roofing: Home,
  "pest-control": Bug,
  remodeling: Hammer,
  landscaping: Trees,
  moving: Truck,
  "garage-door": DoorOpen,
  "appliance-repair": Settings,
};

const ContractorDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [workerId, setWorkerId] = useState(null);
  const [workerEmail, setWorkerEmail] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    business_name: "",
    phone: "",
    description: "",
    hourly_rate: "",
    service_areas: "",
    location: "",
    city: "",
    is_available: true
  });
  
  // Chat state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const profilePhotoRef = useRef(null);
  const workPhotoRef = useRef(null);
  
  // Available jobs state
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  useEffect(() => {
    const savedWorkerId = localStorage.getItem("workerId");
    const savedEmail = localStorage.getItem("workerEmail");
    
    if (savedWorkerId && savedEmail) {
      setWorkerId(savedWorkerId);
      setWorkerEmail(savedEmail);
      fetchDashboard(savedWorkerId);
      fetchConversations(savedWorkerId);
      fetchAvailableJobs(); // Load jobs on initial load
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for conversation param
  useEffect(() => {
    const convId = searchParams.get("conversation");
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === convId);
      if (conv) {
        setSelectedConversation(conv);
        fetchMessages(convId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversations]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchDashboard = async (wId) => {
    try {
      const response = await axios.get(`${API}/dashboard/${wId}`);
      setDashboardData(response.data);
      setEditForm({
        business_name: response.data.profile.business_name || "",
        phone: response.data.profile.phone || "",
        description: response.data.profile.description || "",
        hourly_rate: response.data.profile.hourly_rate || "",
        service_areas: (response.data.profile.service_areas || []).join(", "),
        location: response.data.profile.location || "",
        city: response.data.profile.city || "",
        is_available: response.data.profile.is_available ?? true
      });
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (wId) => {
    try {
      const response = await axios.get(`${API}/conversations/worker/${wId}`);
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(`${API}/messages/${convId}`);
      setMessages(response.data);
      // Mark as read
      await axios.patch(`${API}/conversations/${convId}/read?user_type=worker`);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchAvailableJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await axios.get(`${API}/jobs?status=open`);
      setAvailableJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        conversation_id: selectedConversation.id,
        sender_id: workerId,
        sender_type: "worker",
        sender_name: dashboardData.profile.business_name || dashboardData.profile.name,
        recipient_id: selectedConversation.customer_id,
        recipient_type: "customer",
        message: newMessage
      });
      
      setNewMessage("");
      fetchMessages(selectedConversation.id);
      fetchConversations(workerId);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/workers/${workerId}/photos?photo_type=${type}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${type === "profile" ? "Profile" : "Work"} photo uploaded!`);
      fetchDashboard(workerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemoveWorkPhoto = async (photoUrl) => {
    try {
      await axios.delete(`${API}/workers/${workerId}/photos?photo_url=${encodeURIComponent(photoUrl)}`);
      toast.success("Photo removed");
      fetchDashboard(workerId);
    } catch (error) {
      toast.error("Failed to remove photo");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/workers`);
      const worker = response.data.find(w => w.email.toLowerCase() === workerEmail.toLowerCase());
      
      if (worker) {
        setWorkerId(worker.id);
        localStorage.setItem("workerId", worker.id);
        localStorage.setItem("workerEmail", workerEmail);
        fetchDashboard(worker.id);
        fetchConversations(worker.id);
        fetchAvailableJobs(); // Fetch jobs after login
      } else {
        toast.error("No worker found with this email. Please register first.");
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to login. Please try again.");
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("workerId");
    localStorage.removeItem("workerEmail");
    setWorkerId(null);
    setWorkerEmail("");
    setDashboardData(null);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.patch(`${API}/profile/${workerId}`, {
        business_name: editForm.business_name || null,
        phone: editForm.phone,
        description: editForm.description,
        hourly_rate: parseFloat(editForm.hourly_rate),
        service_areas: editForm.service_areas.split(",").map(s => s.trim()).filter(Boolean),
        location: editForm.location || editForm.city,
        city: editForm.city,
        is_available: editForm.is_available
      });
      toast.success("Profile updated successfully!");
      setShowEditModal(false);
      fetchDashboard(workerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleRequestVerification = async (type) => {
    try {
      await axios.post(`${API}/verify/${workerId}?verification_type=${type}`);
      toast.success(`Verification request submitted for ${type}!`);
      fetchDashboard(workerId);
    } catch (error) {
      toast.error("Failed to request verification");
    }
  };

  // Login screen
  if (!workerId) {
    return (
      <div className="min-h-screen bg-white" data-testid="dashboard-login">
        <Navbar />
        <div className="container-custom py-20">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#e8f0fe] flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-[#1a73e8]" />
              </div>
              <h1 className="text-2xl font-medium text-[#202124] mb-2">Contractor Dashboard</h1>
              <p className="text-[#5f6368]">Manage your profile, leads, and job applications</p>
            </div>

            <form onSubmit={handleLogin} className="form-container" data-testid="dashboard-login-form">
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#202124] mb-2 block">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Your registered email
                </Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={workerEmail}
                  onChange={(e) => setWorkerEmail(e.target.value)}
                  className="h-12 rounded-xl border-[#dadce0]"
                  required
                  data-testid="dashboard-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full py-3 h-auto"
                data-testid="dashboard-login-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Access Dashboard"}
              </Button>

              <p className="text-sm text-[#5f6368] text-center mt-4">
                Not registered?{" "}
                <button type="button" onClick={() => navigate("/join")} className="text-[#1a73e8] hover:underline">
                  Join as a Pro
                </button>
              </p>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-custom py-20 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8]" />
        </div>
      </div>
    );
  }

  const { profile, applications, leads, reviews, contact_requests, stats, subscription } = dashboardData;

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="contractor-dashboard">
      <Navbar />

      {/* Header */}
      <section className="py-6 bg-white border-b border-[#dadce0]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#e8f0fe] flex items-center justify-center overflow-hidden">
                {profile.profile_photo ? (
                  <img src={profile.profile_photo} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-medium text-[#1a73e8]">
                    {profile.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-medium text-[#202124]">
                    {profile.business_name || profile.name}
                  </h1>
                  {profile.is_verified && (
                    <BadgeCheck className="w-5 h-5 text-[#1a73e8]" />
                  )}
                </div>
                <p className="text-[#5f6368] text-sm">{workerEmail}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <NotificationBell 
                userType="contractor" 
                userId={workerId} 
                userEmail={workerEmail}
              />
              <PushNotificationToggle workerId={workerId} />
              {subscription?.plan === "monthly_pro" && subscription?.is_active ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1a73e8] to-[#1557b0] text-white rounded-full text-sm">
                  <Crown className="w-4 h-4" />
                  Pro Plan
                </div>
              ) : (
                <Link
                  to="/leads"
                  className="flex items-center gap-2 px-4 py-2 bg-[#1a73e8] text-white rounded-full text-sm hover:bg-[#1557b0]"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade Plan
                </Link>
              )}
              <Button
                variant="outline"
                onClick={() => setShowEditModal(true)}
                className="rounded-full border-[#dadce0]"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="rounded-full"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-6">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl p-4 border border-[#dadce0]">
              <div className="flex items-center gap-2 text-[#5f6368] mb-2">
                <FileText className="w-4 h-4" />
                <span className="text-sm">Applications</span>
              </div>
              <p className="text-2xl font-medium text-[#202124]">{stats.total_applications}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#dadce0]">
              <div className="flex items-center gap-2 text-[#5f6368] mb-2">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Accepted</span>
              </div>
              <p className="text-2xl font-medium text-[#1e8e3e]">{stats.accepted_applications}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#dadce0]">
              <div className="flex items-center gap-2 text-[#5f6368] mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Leads</span>
              </div>
              <p className="text-2xl font-medium text-[#202124]">{stats.leads_unlocked}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#dadce0]">
              <div className="flex items-center gap-2 text-[#5f6368] mb-2">
                <Star className="w-4 h-4" />
                <span className="text-sm">Rating</span>
              </div>
              <p className="text-2xl font-medium text-[#f9ab00]">{stats.rating?.toFixed(1) || "N/A"}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#dadce0]">
              <div className="flex items-center gap-2 text-[#5f6368] mb-2">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm">Reviews</span>
              </div>
              <p className="text-2xl font-medium text-[#202124]">{stats.reviews_count}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-12">
        <div className="container-custom">
          <Tabs defaultValue="find-jobs" className="space-y-6">
            <TabsList className="bg-white border border-[#dadce0] p-1 rounded-xl flex-wrap">
              <TabsTrigger value="find-jobs" className="rounded-lg flex items-center gap-1 bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Briefcase className="w-4 h-4" />
                Find Jobs
              </TabsTrigger>
              <TabsTrigger value="applications" className="rounded-lg">Applications</TabsTrigger>
              <TabsTrigger value="leads" className="rounded-lg">Leads</TabsTrigger>
              <TabsTrigger value="messages" className="rounded-lg flex items-center gap-1">
                Messages
                {conversations.filter(c => c.unread_worker > 0).length > 0 && (
                  <span className="bg-[#d93025] text-white text-xs px-1.5 py-0.5 rounded-full">
                    {conversations.filter(c => c.unread_worker > 0).length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="photos" className="rounded-lg">Photos</TabsTrigger>
              <TabsTrigger value="calendar" className="rounded-lg flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Availability
              </TabsTrigger>
              <TabsTrigger value="quotes" className="rounded-lg flex items-center gap-1">
                <Calculator className="w-4 h-4" />
                Quotes
              </TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-lg">Reviews</TabsTrigger>
              <TabsTrigger value="verification" className="rounded-lg">Verification</TabsTrigger>
            </TabsList>

            {/* Find Jobs Tab */}
            <TabsContent value="find-jobs" className="mt-0">
              <div className="bg-white rounded-xl border border-[#dadce0] overflow-hidden">
                <div className="p-4 border-b border-[#dadce0] flex items-center justify-between">
                  <div>
                    <h2 className="font-medium text-[#202124]">Available Jobs</h2>
                    <p className="text-sm text-[#5f6368]">Jobs matching your services in your area</p>
                  </div>
                  <Button
                    onClick={fetchAvailableJobs}
                    variant="outline"
                    className="rounded-lg"
                    disabled={loadingJobs}
                  >
                    {loadingJobs ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
                  </Button>
                </div>
                
                {loadingJobs ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8] mx-auto" />
                    <p className="text-[#5f6368] mt-2">Loading jobs...</p>
                  </div>
                ) : availableJobs.length === 0 ? (
                  <div className="p-8 text-center">
                    <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#5f6368]">No jobs available right now</p>
                    <p className="text-sm text-[#5f6368] mt-1">Check back later or expand your service areas</p>
                    <Button 
                      onClick={fetchAvailableJobs}
                      className="mt-4 bg-[#1a73e8] hover:bg-[#1557b0]"
                    >
                      Refresh Jobs
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-[#dadce0] max-h-[600px] overflow-y-auto">
                    {availableJobs.map((job) => {
                      const ServiceIcon = serviceIcons[job.service_category] || Wrench;
                      return (
                        <div key={job.id} className="p-4 hover:bg-[#f8f9fa] transition-colors" data-testid={`job-${job.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#e8f0fe] flex items-center justify-center flex-shrink-0">
                                <ServiceIcon className="w-5 h-5 text-[#1a73e8]" />
                              </div>
                              <div>
                                <h3 className="font-medium text-[#202124]">{job.title}</h3>
                                <div className="flex items-center gap-3 text-sm text-[#5f6368] mt-1">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {job.location}
                                  </span>
                                  {job.budget_min && job.budget_max && (
                                    <span className="flex items-center gap-1">
                                      <DollarSign className="w-3 h-3" />
                                      ${job.budget_min} - ${job.budget_max}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-[#5f6368] mt-2 line-clamp-2">{job.description}</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#e8f0fe] text-[#1a73e8]">
                                {job.service_category?.replace("-", " ")}
                              </span>
                              <span className="text-xs text-[#5f6368]">
                                {new Date(job.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-xs text-[#5f6368]">
                              {job.applications_count || 0} applications
                            </span>
                            <Link
                              to={`/jobs?apply=${job.id}`}
                              className="bg-[#1a73e8] hover:bg-[#1557b0] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
                            >
                              Apply Now
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="p-4 border-t border-[#dadce0] bg-[#f8f9fa]">
                  <Link
                    to="/jobs"
                    className="text-[#1a73e8] text-sm hover:underline flex items-center justify-center gap-1"
                  >
                    View All Jobs on Job Board
                    <TrendingUp className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications">
              <div className="bg-white rounded-xl border border-[#dadce0] overflow-hidden">
                <div className="p-4 border-b border-[#dadce0] flex items-center justify-between">
                  <h2 className="font-medium text-[#202124]">Your Job Applications</h2>
                  <Link
                    to="/jobs"
                    className="text-[#1a73e8] text-sm hover:underline"
                  >
                    Browse Jobs
                  </Link>
                </div>
                {applications.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#5f6368]">No job applications yet</p>
                    <Link
                      to="/jobs"
                      className="text-[#1a73e8] text-sm hover:underline mt-2 inline-block"
                    >
                      Browse available jobs
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-[#dadce0]">
                    {applications.map((app) => (
                      <div key={app.id} className="p-4" data-testid={`application-${app.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-[#202124]">{app.job?.title || "Job"}</h3>
                            <p className="text-sm text-[#5f6368]">{app.job?.location}</p>
                            {app.proposed_price && (
                              <p className="text-sm text-[#1a73e8]">Your quote: ${app.proposed_price}</p>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            app.status === "accepted" 
                              ? "bg-[#e6f4ea] text-[#1e8e3e]" 
                              : app.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-[#fef7e0] text-[#b06000]"
                          }`}>
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-[#5f6368] mt-2 line-clamp-2">{app.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Leads Tab */}
            <TabsContent value="leads">
              <div className="bg-white rounded-xl border border-[#dadce0] overflow-hidden">
                <div className="p-4 border-b border-[#dadce0] flex items-center justify-between">
                  <h2 className="font-medium text-[#202124]">Unlocked Leads</h2>
                  <Link to="/leads" className="text-[#1a73e8] text-sm hover:underline">
                    View All Leads
                  </Link>
                </div>
                {leads.length === 0 ? (
                  <div className="p-8 text-center">
                    <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#5f6368]">No leads unlocked yet</p>
                    <Link
                      to="/leads"
                      className="text-[#1a73e8] text-sm hover:underline mt-2 inline-block"
                    >
                      Browse available leads
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-[#dadce0]">
                    {leads.map((lead) => (
                      <div key={lead.id} className="p-4" data-testid={`lead-${lead.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-[#202124]">{lead.customer_name}</p>
                            <p className="text-sm text-[#5f6368]">{lead.service_category} - {lead.location}</p>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={`tel:${lead.customer_phone}`}
                              className="p-2 bg-[#e6f4ea] text-[#1e8e3e] rounded-full hover:bg-[#ceead6]"
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                            <a
                              href={`mailto:${lead.customer_email}`}
                              className="p-2 bg-[#e8f0fe] text-[#1a73e8] rounded-full hover:bg-[#d2e3fc]"
                            >
                              <Mail className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                        <p className="text-sm text-[#5f6368] mt-2 line-clamp-2">{lead.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Availability Calendar Tab */}
            <TabsContent value="calendar">
              <div className="grid md:grid-cols-2 gap-6">
                <AvailabilityCalendar workerId={workerId} editable={true} />
                <ServiceAreaMap workerId={workerId} editable={true} />
              </div>
            </TabsContent>

            {/* Quotes Tab */}
            <TabsContent value="quotes">
              <div className="bg-white rounded-xl border border-[#dadce0] p-6">
                <QuoteBuilder workerId={workerId} />
              </div>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <div className="bg-white rounded-xl border border-[#dadce0] overflow-hidden">
                <div className="p-4 border-b border-[#dadce0]">
                  <h2 className="font-medium text-[#202124]">Customer Reviews</h2>
                </div>
                {reviews.length === 0 ? (
                  <div className="p-8 text-center">
                    <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#5f6368]">No reviews yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#dadce0]">
                    {reviews.map((review) => (
                      <div key={review.id} className="p-4" data-testid={`review-${review.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-[#202124]">{review.customer_name}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < review.rating
                                      ? "text-[#f9ab00] fill-[#f9ab00]"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <span className="text-sm text-[#5f6368]">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-[#5f6368]">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages">
              <div className="bg-white rounded-xl border border-[#dadce0] overflow-hidden">
                <div className="grid md:grid-cols-3 h-[500px]">
                  {/* Conversation List */}
                  <div className="border-r border-[#dadce0] overflow-y-auto">
                    <div className="p-4 border-b border-[#dadce0]">
                      <h2 className="font-medium text-[#202124]">Conversations</h2>
                    </div>
                    {conversations.length === 0 ? (
                      <div className="p-8 text-center">
                        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-[#5f6368] text-sm">No messages yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[#dadce0]">
                        {conversations.map((conv) => (
                          <button
                            key={conv.id}
                            onClick={() => {
                              setSelectedConversation(conv);
                              fetchMessages(conv.id);
                            }}
                            className={`w-full p-4 text-left hover:bg-[#f8f9fa] transition-colors ${
                              selectedConversation?.id === conv.id ? "bg-[#e8f0fe]" : ""
                            }`}
                            data-testid={`conversation-${conv.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-[#202124]">{conv.customer_name || "Customer"}</p>
                                <p className="text-sm text-[#5f6368] truncate">{conv.last_message || "No messages"}</p>
                              </div>
                              {conv.unread_worker > 0 && (
                                <span className="bg-[#1a73e8] text-white text-xs px-2 py-0.5 rounded-full">
                                  {conv.unread_worker}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Chat Area */}
                  <div className="md:col-span-2 flex flex-col">
                    {selectedConversation ? (
                      <>
                        {/* Chat Header */}
                        <div className="p-4 border-b border-[#dadce0]">
                          <p className="font-medium text-[#202124]">{selectedConversation.customer_name || "Customer"}</p>
                        </div>
                        
                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                          {messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_type === "worker" ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[70%] p-3 rounded-2xl ${
                                  msg.sender_type === "worker"
                                    ? "bg-[#1a73e8] text-white"
                                    : "bg-[#f8f9fa] text-[#202124]"
                                }`}
                              >
                                <p className="text-sm">{msg.message}</p>
                                <p className={`text-xs mt-1 ${msg.sender_type === "worker" ? "text-white/70" : "text-[#5f6368]"}`}>
                                  {new Date(msg.created_at).toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                        
                        {/* Message Input */}
                        <form onSubmit={handleSendMessage} className="p-4 border-t border-[#dadce0]">
                          <div className="flex gap-2">
                            <Input
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              placeholder="Type a message..."
                              className="flex-1 rounded-full"
                              data-testid="message-input"
                            />
                            <Button
                              type="submit"
                              disabled={sendingMessage || !newMessage.trim()}
                              className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
                              data-testid="send-message-btn"
                            >
                              {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                          </div>
                        </form>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-[#5f6368]">Select a conversation to view messages</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent value="photos">
              <div className="bg-white rounded-xl border border-[#dadce0] p-6">
                <h2 className="font-medium text-[#202124] mb-6">Manage Photos</h2>
                
                {/* Profile Photo */}
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-[#5f6368] mb-4">Profile Photo</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-xl bg-[#e8f0fe] flex items-center justify-center overflow-hidden">
                      {profile.profile_photo ? (
                        <img src={profile.profile_photo} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-10 h-10 text-[#1a73e8]" />
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        ref={profilePhotoRef}
                        onChange={(e) => handlePhotoUpload(e, "profile")}
                        accept="image/*"
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        onClick={() => profilePhotoRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="rounded-full border-[#dadce0]"
                        data-testid="upload-profile-photo-btn"
                      >
                        {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                        {profile.profile_photo ? "Change Photo" : "Upload Photo"}
                      </Button>
                      <p className="text-xs text-[#5f6368] mt-2">Recommended: Square image, at least 200x200px</p>
                    </div>
                  </div>
                </div>
                
                {/* Work Photos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-[#5f6368]">Work Samples</h3>
                    <input
                      type="file"
                      ref={workPhotoRef}
                      onChange={(e) => handlePhotoUpload(e, "work")}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => workPhotoRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="rounded-full border-[#dadce0]"
                      data-testid="upload-work-photo-btn"
                    >
                      {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Add Photo
                    </Button>
                  </div>
                  
                  {profile.work_photos?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {profile.work_photos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={photo}
                            alt={`Work sample ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-xl"
                          />
                          <button
                            onClick={() => handleRemoveWorkPhoto(photo)}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-photo-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-[#f8f9fa] rounded-xl">
                      <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-[#5f6368]">No work photos yet</p>
                      <p className="text-sm text-[#5f6368] mt-1">Add photos to showcase your work to customers</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Verification Tab */}
            <TabsContent value="verification">
              <div className="bg-white rounded-xl border border-[#dadce0] p-6">
                <h2 className="font-medium text-[#202124] mb-6">Verification Badges</h2>
                <p className="text-[#5f6368] mb-6">
                  Get verified to build trust with customers and stand out from the competition.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl border ${profile.is_verified ? "border-[#1e8e3e] bg-[#e6f4ea]" : "border-[#dadce0]"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <BadgeCheck className={`w-5 h-5 ${profile.is_verified ? "text-[#1e8e3e]" : "text-[#5f6368]"}`} />
                        <span className="font-medium text-[#202124]">Verified Pro</span>
                      </div>
                      {profile.is_verified ? (
                        <CheckCircle className="w-5 h-5 text-[#1e8e3e]" />
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRequestVerification("verified")}
                          className="rounded-full"
                        >
                          Request
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-[#5f6368]">Basic identity verification</p>
                  </div>

                  <div className={`p-4 rounded-xl border ${profile.is_licensed ? "border-[#1e8e3e] bg-[#e6f4ea]" : "border-[#dadce0]"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText className={`w-5 h-5 ${profile.is_licensed ? "text-[#1e8e3e]" : "text-[#5f6368]"}`} />
                        <span className="font-medium text-[#202124]">Licensed</span>
                      </div>
                      {profile.is_licensed ? (
                        <CheckCircle className="w-5 h-5 text-[#1e8e3e]" />
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRequestVerification("licensed")}
                          className="rounded-full"
                        >
                          Request
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-[#5f6368]">Professional license verified</p>
                  </div>

                  <div className={`p-4 rounded-xl border ${profile.is_background_checked ? "border-[#1e8e3e] bg-[#e6f4ea]" : "border-[#dadce0]"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Shield className={`w-5 h-5 ${profile.is_background_checked ? "text-[#1e8e3e]" : "text-[#5f6368]"}`} />
                        <span className="font-medium text-[#202124]">Background Checked</span>
                      </div>
                      {profile.is_background_checked ? (
                        <CheckCircle className="w-5 h-5 text-[#1e8e3e]" />
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRequestVerification("background_checked")}
                          className="rounded-full"
                        >
                          Request
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-[#5f6368]">Clean background check</p>
                  </div>

                  <div className={`p-4 rounded-xl border ${profile.is_insured ? "border-[#1e8e3e] bg-[#e6f4ea]" : "border-[#dadce0]"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-5 h-5 ${profile.is_insured ? "text-[#1e8e3e]" : "text-[#5f6368]"}`} />
                        <span className="font-medium text-[#202124]">Insured</span>
                      </div>
                      {profile.is_insured ? (
                        <CheckCircle className="w-5 h-5 text-[#1e8e3e]" />
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRequestVerification("insured")}
                          className="rounded-full"
                        >
                          Request
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-[#5f6368]">Liability insurance verified</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto sm:max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Edit Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveProfile} className="space-y-4 pb-2">
            <div>
              <Label className="text-sm font-medium text-gray-700">Business Name (optional)</Label>
              <Input
                value={editForm.business_name}
                onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                placeholder="Your Company LLC"
                className="mt-1"
                data-testid="edit-business-name"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                required
                className="mt-1"
                data-testid="edit-phone"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Hourly Rate ($)</Label>
              <Input
                type="number"
                value={editForm.hourly_rate}
                onChange={(e) => setEditForm({...editForm, hourly_rate: e.target.value})}
                required
                className="mt-1"
                data-testid="edit-hourly-rate"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">
                <MapPin className="w-4 h-4 inline mr-1" />
                Your Location
              </Label>
              <select
                value={editForm.city}
                onChange={(e) => setEditForm({...editForm, city: e.target.value, location: e.target.value})}
                className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="edit-location"
              >
                <option value="">Select your city...</option>
                <option value="Phoenix, AZ">Phoenix, AZ</option>
                <option value="Scottsdale, AZ">Scottsdale, AZ</option>
                <option value="Tempe, AZ">Tempe, AZ</option>
                <option value="Mesa, AZ">Mesa, AZ</option>
                <option value="Chandler, AZ">Chandler, AZ</option>
                <option value="Gilbert, AZ">Gilbert, AZ</option>
                <option value="Glendale, AZ">Glendale, AZ</option>
                <option value="Peoria, AZ">Peoria, AZ</option>
                <option value="Surprise, AZ">Surprise, AZ</option>
                <option value="Avondale, AZ">Avondale, AZ</option>
                <option value="Goodyear, AZ">Goodyear, AZ</option>
                <option value="Buckeye, AZ">Buckeye, AZ</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">This helps homeowners find you on the map</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Service Areas (comma separated)</Label>
              <Input
                value={editForm.service_areas}
                onChange={(e) => setEditForm({...editForm, service_areas: e.target.value})}
                placeholder="Phoenix, Scottsdale, Tempe"
                className="mt-1"
                data-testid="edit-service-areas"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                rows={3}
                required
                className="mt-1"
                data-testid="edit-description"
              />
            </div>
            <div className="flex items-center justify-between py-2 px-1 bg-gray-50 rounded-lg">
              <Label className="text-sm font-medium text-gray-700">Available for work</Label>
              <Switch
                checked={editForm.is_available}
                onCheckedChange={(checked) => setEditForm({...editForm, is_available: checked})}
                data-testid="edit-availability"
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-[#1a73e8] hover:bg-[#1557b0] rounded-full py-3 mt-2"
              data-testid="save-profile-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ContractorDashboard;
